import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { sendCustomTrackingInfo, ModalBase } from '@panel/commons';
import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import uuid from 'uuid-random';
import { selectColor } from '@panel/commons/core/theme';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';
import { formatNumber } from '@panel/commons/helpers/formatAmount';
import { isBrasilSite } from '@panel/commons/helpers/siteUtils';
import SectionLayout from '../SectionLayout';
import TooltipHelper from '../../assets/icons/tooltipHelper.svg';
import IconRatings from '../../assets/icons/ratingsIcon.svg';
import CalendarIcon from '../../assets/icons/calendarioGray.svg';
import StarIcon from '../../assets/icons/star.svg';
import ChevronDownIcon from '../../assets/icons/chevron_down.svg';
import ChevronUpIcon from '../../assets/icons/chevron_up.svg';
import StarsRating from '../StarsRating';
import { ReputationSelectors } from '../../ducks/reputation';
import { dataRatingsAdapter } from '../dataRatingsAdapter';
import EmptyStateCharts from '../EmptyState';
import { redirectToHelp } from '../../helpers/ratingsHelpLinks';
import { Modal } from '../Modals/ModalWrapper';
import Comment from '../Comment';
import {
  Container,
  HeaderArea,
  Icon,
  TitleText,
  TooltipComponent,
  IconTooltip,
  ContainerInfo,
  IconInfo,
  MainArea,
  LeftSide,
  ContainerRaitings,
  Rating,
  RightSide,
  ProgressBarContainer,
  RatingText,
  ProgressBarRow,
  MessageContainer,
  Alert,
  Message,
  TotalvalueContainer,
  TotalValueSpan,
  LinkToHelp,
  ContainerValue,
  BottomSheetContainer,
  BottomSheetTitle,
  MoreComments,
  ChevronDown,
  ModalContainer,
  CommentEmptyState,
  CommentsContainer
} from './styles';
import './buttonSheetStyles.css';

const Ratings = ({ device, reloadDispatch }) => {
  const { t } = useTranslation();
  const isMobile = device === 'mobile';
  const [ratingsData, setRatingsData] = useState({});
  const [progressBarData, setProgressBarData] = useState([]);
  const [openBottomSheet, setOpenBottomSheet] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const linkToHelp = redirectToHelp;
  const { ratings, ratingsError, customersComments } = useSelector(ReputationSelectors.getReputationState);

  const {
    generalScore,
    totalRatings,
    responseTime,
    postingContent,
    attention,
    recommendYou,
    notRespondedCount
  } = ratingsData;

  useEffect(() => {
    if (ratings) setRatingsData(dataRatingsAdapter(ratings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratings]);

  useEffect(() => {
    setProgressBarData([
      { value: postingContent },
      { value: responseTime },
      { value: attention },
      { value: recommendYou }
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingsData]);

  const tooltipComponentHeader = () => {
    return <TooltipComponent>{t(`panel.estadisticas.ratings.tooltip`)}</TooltipComponent>;
  };
  const getInfoTotalValues = () => {
    if (totalRatings > 0) {
      return (
        <TotalValueSpan device={device} data-panel='ratings-Totalvalue'>
          {t(`panel.estadisticas.ratings.avgText.total${totalRatings > 1 ? '.plural' : ''}`, {
            value: totalRatings || '0'
          })}
        </TotalValueSpan>
      );
    }
    return <div className='emptyState'>{t('panel.estadisticas.ratings.emptyStateText')}</div>;
  };

  const formatValue = (value, twoDecimals) => {
    if (!value || (value && value === 0)) return '-';
    const arrayValue = value.toString().split('.');
    // Se añade un '_,0' en caso de que el valor del servicio no venga con decimales:
    if (value && !arrayValue[1] && !twoDecimals) return formatNumber(`${arrayValue[0]},0`);
    // Se añade un '_,00' en caso de que el valor del servicio no venga con decimales:
    if (value && !arrayValue[1] && twoDecimals) return formatNumber(`${arrayValue[0]},00`);
    // Se añade un '_,_0' en caso de que el valor del servicio venga con un sólo decimal:
    if (value && arrayValue[1].length === 1 && twoDecimals)
      return formatNumber(`${arrayValue[0]},${arrayValue[1].padEnd(2, '0')}`);
    return formatNumber(value);
  };

  const getTopEmptyState = () => {
    if (device === 'mobile') return '20px';
    if (device === 'desktop') return '0px';
    return '30px';
  };

  return (
    <SectionLayout
      data-panel='panel-estadisticas-ratings'
      marginTop='0px'
      paddingBottom={notRespondedCount ? '24' : null}
    >
      <Container>
        <HeaderArea>
          <Icon icon={IconRatings} data-panel='panel-estadisticas-ratings-icon' />
          <TitleText data-panel='panel-estadisticas-ratings-title'>{t('panel.estadisticas.ratings.title')}</TitleText>
          <SimpleTooltip
            offset={5}
            backgroundColor={selectColor('black')}
            isMobile={false}
            text={tooltipComponentHeader()}
            position='top-center'
            dataPanel='panel-estadisticas-ratings-tooltip'
            allowPlacements='top-end'
            autoPlacements
          >
            <IconTooltip
              src={TooltipHelper}
              alt='tooltip-helper'
              width='16'
              height='16'
              data-panel='panel-estadisticas-ratings-icon-tooltip'
            />
          </SimpleTooltip>
          {!isMobile && (
            <ContainerInfo device={device} ratingsError={ratingsError}>
              <IconInfo src={CalendarIcon} data-panel='panel-estadisticas-ratings-icon-tooltip' />
              <div data-panel='ratings-info-text'>{t(`panel.estadisticas.ratings.info`)}</div>
            </ContainerInfo>
          )}
        </HeaderArea>
        {!ratingsError ? (
          <MainArea device={device} notRespondedCount={notRespondedCount}>
            <LeftSide device={device} data-panel='ratings-container-leftSide'>
              <ContainerRaitings device={device} generalScore={generalScore}>
                <ContainerValue data-panel='ratings-value'>{formatValue(generalScore, true)}</ContainerValue>
                <Rating>
                  <StarsRating
                    width='40px'
                    height='38px'
                    icon={StarIcon}
                    value={generalScore}
                    numOfStars='5'
                    dataPanel='container-ratings-stars'
                  />
                  <TotalvalueContainer data-panel='ratings-container-Totalvalue'>
                    {getInfoTotalValues()}
                  </TotalvalueContainer>
                </Rating>
              </ContainerRaitings>
              {progressBarData &&
                progressBarData.map((element, i) => {
                  return (
                    <ProgressBarContainer device={device} key={i.toString()}>
                      <RatingText device={device} data-panel={`progressBar-text-${i}`} isBrasilSite={isBrasilSite}>
                        {t(`panel.estadisticas.ratings.question.${i + 1}`)}
                      </RatingText>
                      <ProgressBarRow device={device} isBrasilSite={isBrasilSite}>
                        <StarsRating
                          width='24px'
                          height='23px'
                          icon={StarIcon}
                          value={element.value}
                          numOfStars='5'
                          dataPanel='container-ratings-stars'
                          backgroundColorStar
                        />
                        <div className='containerValue'>
                          <div className='value' data-panel={`progressBar-value-${i}`}>
                            {formatValue(element.value)}
                          </div>
                        </div>
                      </ProgressBarRow>
                    </ProgressBarContainer>
                  );
                })}
              <LinkToHelp
                device={device}
                href={linkToHelp}
                target='_blank'
                rel='noopener noreferrer'
                data-panel='ratings-info-linkToHelp'
                onClick={() =>
                  sendCustomTrackingInfo({
                    event: 'select_content',
                    content_type: 'Estadisticas - Reputación',
                    item_id: 'Ayuda'
                  })
                }
              >
                {t('panel.estadisticas.ratings.linkToHelp')}
              </LinkToHelp>
            </LeftSide>
            <RightSide device={device} data-panel='ratings-container-rightSide'>
              {(!customersComments || customersComments.length === 0) && (
                <CommentEmptyState device={device}>{t('panel.estadisticas.ratings.emptyState')}</CommentEmptyState>
              )}
              {customersComments &&
                customersComments.map((customerComment, index) => {
                  if ((isMobile && index < 1) || (!isMobile && index < 2)) {
                    return <Comment key={uuid()} customerComment={customerComment} />;
                  }
                  return null;
                })}
              {customersComments && ((isMobile && customersComments.length > 1) || customersComments.length > 2) && (
                <MoreComments
                  onClick={() => {
                    if (isMobile) setOpenBottomSheet(true);
                    if (!isMobile) setShowModal(true);
                    sendCustomTrackingInfo({
                      event: 'select_content',
                      content_type: 'Estadisticas - Reputación',
                      item_id: 'Ver comentarios'
                    });
                  }}
                >
                  {t('panel.estadisticas.ratings.moreComments')}{' '}
                  <ChevronDown
                    data-panel='ratings-moreComments-chevron'
                    src={showModal || openBottomSheet ? ChevronUpIcon : ChevronDownIcon}
                    alt='chevron-down'
                  />
                </MoreComments>
              )}
            </RightSide>
            {showModal && !isMobile && (
              <Modal>
                <ModalBase
                  openModal={showModal}
                  onClose={() => setShowModal(false)}
                  width='588px'
                  height='562px'
                  rightPositionClose='25px'
                  topPositionClose='25px'
                >
                  <ModalContainer>
                    <TitleText data-panel='ratings-moreComments-modal-title' mBottom='32px' fontSize='20px'>
                      {t('panel.estadisticas.ratings.moreComments.title')}
                    </TitleText>
                    <CommentsContainer data-panel='ratings-moreComments-modal-container'>
                      {customersComments &&
                        customersComments.map(customerComment => (
                          <Comment key={uuid()} customerComment={customerComment} keyId={uuid()} />
                        ))}
                    </CommentsContainer>
                    {/* <MoreComments onClick={() => setShowModal(false)}>
                    Ver menos comentarios <ChevronDown src={ChevronDownIcon} alt='down' />
                  </MoreComments> */}
                  </ModalContainer>
                </ModalBase>
              </Modal>
            )}
            {isMobile && (
              <BottomSheet
                open={openBottomSheet}
                onDismiss={() => setOpenBottomSheet(false)}
                snapPoints={({ maxHeight }) => [maxHeight / 1.12]}
                header={React.createElement(
                  'div',
                  {},
                  <BottomSheetTitle data-panel='ratings-bottomSheet-title'>
                    {t('panel.estadisticas.ratings.moreComments.title')}
                  </BottomSheetTitle>
                )}
              >
                <BottomSheetContainer>
                  <CommentsContainer height='429px' data-panel='ratings-bottomSheet-container'>
                    {customersComments &&
                      customersComments.map(customerComment => (
                        <Comment key={uuid()} customerComment={customerComment} keyId={uuid()} />
                      ))}
                  </CommentsContainer>
                </BottomSheetContainer>
              </BottomSheet>
            )}
          </MainArea>
        ) : (
          <EmptyStateCharts
            withSpacing={device === 'desktop'}
            height={isMobile ? '160px' : '115px'}
            top={getTopEmptyState()}
            callback={reloadDispatch}
          />
        )}
      </Container>
      {notRespondedCount && notRespondedCount > 0 && !ratingsError ? (
        <MessageContainer device={device}>
          <Alert />
          {notRespondedCount === 1 ? (
            <Message>
              <b>{t('panel.estadisticas.ratings.banner', { value: notRespondedCount })}</b>
              <span>{t(`panel.estadisticas.ratings.banner.post${isMobile ? '.mobile' : ''}`)}</span>
            </Message>
          ) : (
            <Message>
              {t('panel.estadisticas.ratings.banner.pre.plural')}
              <b>{t('panel.estadisticas.ratings.banner.plural', { value: notRespondedCount })}</b>
              <span>{t(`panel.estadisticas.ratings.banner.post.plural${isMobile ? '.mobile' : ''}`)}</span>
            </Message>
          )}
        </MessageContainer>
      ) : null}
    </SectionLayout>
  );
};

export default Ratings;
